import { useCallback, useMemo } from "react";
import {
  QueryFunction,
  QueryFunctionContext,
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "react-query";
import { AccessEnum } from "~/api/GeneratedApi";
import { CustomQueryKey, SearchType } from "~/utils/hooks/QueryKeys";
import { useAccess } from "./useAccess";
import { useApiErrorHandler } from "./useApiErrorHandler";

export type Id = {
  id: number | null | undefined;
};

type AllowedObject =
  | {
      [key: string]: number | undefined | null | string;
    }
  | SearchType<unknown>;

type Combine<T extends AllowedObject, N extends AllowedObject> = {
  [key in keyof T]: T[key];
} & {
  [key in keyof N]: N[key];
};

export type WithParameters<T extends string, N extends AllowedObject> = Combine<
  { key: T },
  N
> extends infer R
  ? R /*  | UnionToTuple<{ [key in keyof R]: R[key] }[keyof R]> */
  : never;

/* type asd = WithParameters<"asd", Id>; */

/* type UnionToIntersection<U> = (
  U extends never ? never : (arg: U) => never
) extends (arg: infer I) => void
  ? I
  : never;

type UnionToTuple<T> = UnionToIntersection<
  T extends never ? never : (t: T) => T
> extends (_: never) => infer W
  ? [...UnionToTuple<Exclude<T, W>>, W]
  : []; */

export function querykeyToArray(key: CustomQueryKey): QueryKey {
  return typeof key === "string"
    ? [key]
    : (Object.keys(key) as (keyof CustomQueryKey)[]).sort().map((x) => key[x]);
}

export function useCustomQuery<TQueryFnData, TError, TData = TQueryFnData>(
  queryKey: CustomQueryKey,
  queryFn: QueryFunction<Promise<TQueryFnData>, QueryKey>,
  options?: UseQueryOptions<TQueryFnData, TError, TData, QueryKey> & {
    requiredAccess?: AccessEnum;
    disableErrorNoti?: boolean;
  },
): UseQueryResult<TData, TError> {
  const handleApiError = useApiErrorHandler();
  const { accesses } = useAccess();

  const fn = useCallback(
    (context: QueryFunctionContext<QueryKey, unknown>) =>
      new Promise<TQueryFnData>((resolve, reject) => {
        queryFn(context)
          .then(resolve)
          .catch((err) => {
            if (!options?.disableErrorNoti) handleApiError(err as Error);
            reject(err);
          });
      }),
    [queryFn, options?.disableErrorNoti, handleApiError],
  );

  const key = useMemo(() => querykeyToArray(queryKey), [queryKey]);

  return useQuery(key, fn, {
    ...options,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    retry: options?.retry ?? false,
    enabled:
      (options?.enabled ?? true) &&
      (options?.requiredAccess ? accesses[options.requiredAccess] : true),
  });
}

export const staleTime30mins = 1800000;
